// src/components/TopBar.jsx
import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Box,
  Menu,
  MenuItem,
  InputBase,
  styled,
  alpha,
} from "@mui/material";

import FigmaIcon from "./icons/FigmaIcon";
import HeaderProfile from "./HeaderProfile";
import HeaderQuickActions from "./HeaderQuickActions";

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": { backgroundColor: alpha(theme.palette.common.white, 0.25) },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: "100%",
  [theme.breakpoints.up("sm")]: { marginLeft: theme.spacing(3), width: "auto" },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    width: "100%",
    [theme.breakpoints.up("md")]: { width: "20ch" },
  },
}));

export default function TopBar({
  user,
  onLogout,
  anchorEl,
  onProfileMenuOpen,
  onProfileMenuClose,
  onCandidateCreated, // ✅ add this
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const [globalQuery, setGlobalQuery] = useState("");
  useEffect(() => {
    if (location.pathname !== "/search") return;
    const params = new URLSearchParams(location.search);
    setGlobalQuery(params.get("q") || "");
  }, [location.pathname, location.search]);

  const goToMyUserView = useMemo(() => {
    return () => {
      const userId = user?.id || user?.user_id;
      if (!userId) return;

      navigate(`/users/${encodeURIComponent(String(userId))}`, {
        state: { recruiter: user },
      });
    };
  }, [navigate, user]);

  return (
    <AppBar
      className="boxCard no-padding"
      position="static"
      color="default"
      elevation={1}
      sx={{ mb: 2 }}
    >
      <Toolbar
        className="boxcard-wrapper search-bar-wrapper"
        sx={{ display: "flex", alignItems: "center" }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Search>
            <SearchIconWrapper>
              <FigmaIcon name="headerSearch" color="#9C4FFF" />
            </SearchIconWrapper>

            <StyledInputBase
              className="search-input"
              placeholder="Search…"
              inputProps={{ "aria-label": "search" }}
              value={globalQuery}
              onChange={(e) => setGlobalQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const q = globalQuery.trim();
                  if (!q) return;
                  navigate(`/search?q=${encodeURIComponent(q)}`);
                }
              }}
            />
          </Search>
        </Box>

        <Box
          className="actions-main-wrapper"
          sx={{ display: "flex", alignItems: "center", ml: 2 }}
        >
          <Box
            className="topbar-action-menu"
            sx={{ display: "flex", alignItems: "center", ml: 2 }}
          >
            <Box
              className="action-buttons-wrapper"
              sx={{ display: "flex", alignItems: "center", gap: 1, ml: 2 }}
            >
              <HeaderQuickActions
                user={user}
                onCandidateCreated={onCandidateCreated}
              />
            </Box>
          </Box>

          <HeaderProfile user={user} onProfileMenuOpen={onProfileMenuOpen} />
        </Box>
      </Toolbar>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onProfileMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            onProfileMenuClose();
            goToMyUserView();
          }}
        >
          Profile
        </MenuItem>

        <MenuItem
          onClick={() => {
            onProfileMenuClose();
            onLogout();
          }}
        >
          Logout
        </MenuItem>
      </Menu>

    </AppBar>
  );
}
